import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import type { UserRow } from "@/hooks/use-users";
import {
  isUserRole,
  ROLE_DESCRIPTIONS,
  USER_ROLES,
  useChangeRole,
  useResetRoleSource,
} from "@/hooks/use-users";

interface ChangeRoleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: UserRow;
  /** What automatic mapping would give this user, when a rule matches them. */
  mappedRole?: string | null;
}

/**
 * Change a role by hand.
 *
 * The reason field is required, and deliberately so: this is the most consequential
 * thing the Users screen does, it is what the audit trail gets read for afterwards, and
 * the cost of asking is one sentence.
 *
 * Saving here also pins the role to MANUAL — automatic mapping will not move it again
 * until someone chooses "back to automatic" below.
 */
export function ChangeRoleDialog({ open, onOpenChange, user, mappedRole }: ChangeRoleDialogProps) {
  const [role, setRole] = useState(isUserRole(user.role) ? user.role : "SA");
  const [reason, setReason] = useState("");
  const changeRole = useChangeRole();
  const resetRoleSource = useResetRoleSource();

  const unchanged = role === user.role;
  const reasonMissing = reason.trim().length === 0;

  function handleSubmit() {
    if (unchanged || reasonMissing) return;
    changeRole.mutate(
      { userId: user.id, role, reason: reason.trim() },
      {
        onSuccess: () => {
          setReason("");
          onOpenChange(false);
        },
      },
    );
  }

  function handleReset() {
    resetRoleSource.mutate({ userId: user.id }, { onSuccess: () => onOpenChange(false) });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Change role</DialogTitle>
          <DialogDescription>
            Set the role for {user.name}. This is recorded against your name.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="role-select">Role</Label>
            <Select
              value={role}
              onValueChange={(value) => {
                if (isUserRole(value)) setRole(value);
              }}
            >
              <SelectTrigger id="role-select" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {USER_ROLES.map((r) => (
                  <SelectItem key={r} value={r}>
                    {r}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {isUserRole(role) && (
              <p className="text-xs text-muted-foreground">{ROLE_DESCRIPTIONS[role]}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="role-reason">Reason</Label>
            <Textarea
              id="role-reason"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="Why is this changing? e.g. moved to the Accounts team"
              rows={3}
            />
            <p className="text-xs text-muted-foreground">
              Saved to the audit trail alongside the change.
            </p>
          </div>

          {user.roleSource === "manual" && (
            <div className="rounded-md border border-border bg-muted/40 p-3">
              <p className="text-sm font-medium">This role was set by hand</p>
              <p className="mt-1 text-xs text-muted-foreground">
                Automatic mapping will leave it alone.{" "}
                {mappedRole
                  ? `Handing it back would make them ${mappedRole}.`
                  : "No rule currently matches them, so handing it back would leave the role as it is."}
              </p>
              <Button
                variant="outline"
                size="sm"
                className="mt-3"
                onClick={handleReset}
                disabled={resetRoleSource.isPending}
              >
                {resetRoleSource.isPending ? "Resetting…" : "Back to automatic"}
              </Button>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={changeRole.isPending || unchanged || reasonMissing}
          >
            {changeRole.isPending ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
